import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Wrench, X, IndianRupee, TrendingUp, Gauge } from "lucide-react";
import FileUploader from "@/components/FileUploader";

const inputCls =
  "w-full bg-white border border-[#e5e0d8] text-[#1c1a17] rounded-sm px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#234333] focus:border-[#234333]";

const SERVICE_TYPES = ["Periodic", "Repair", "Body Work", "Tyre", "Battery", "Insurance Claim", "Fitness Renewal", "Other"];

const emptyLog = {
  id: "",
  date: "",
  workshop: "",
  service_type: "Periodic",
  description: "",
  odometer_km: 0,
  cost: 0,
  next_service_km: 0,
  next_service_date: "",
  invoice_url: "",
  invoice_public_id: "",
};

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-3" : ""}`}>
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-2">{label}</div>
      {children}
    </label>
  );
}

const fmtInr = (n) => `₹ ${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

export default function VehicleServicesDialog({ vehicle, canEdit, onClose, onDirty }) {
  const open = !!vehicle;
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyLog);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    if (!vehicle) return;
    setLoading(true);
    api.get(`/admin/vehicles/${vehicle.id}/services`)
      .then(({ data }) => setLogs(data))
      .catch((err) => toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Failed to load service history"))
      .finally(() => setLoading(false));
  }, [vehicle]);

  const tco = useMemo(() => {
    const service = logs.reduce((s, l) => s + Number(l.cost || 0), 0);
    const latest = logs[0]; // sorted by date desc from server
    return {
      service_count: logs.length,
      service_cost: service,
      purchase: Number(vehicle?.purchase_price || 0),
      total: service + Number(vehicle?.purchase_price || 0),
      latest_date: latest?.date || "",
      latest_workshop: latest?.workshop || "",
      next_service_km: latest?.next_service_km || 0,
      next_service_date: latest?.next_service_date || "",
    };
  }, [logs, vehicle]);

  const openAdd = () => { setForm({ ...emptyLog, date: new Date().toISOString().slice(0, 10), odometer_km: vehicle?.odometer_km || 0 }); setShowForm(true); };
  const openEdit = (l) => { setForm({ ...emptyLog, ...l }); setShowForm(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.date) return toast.error("Service date is required");
    const payload = {
      date: form.date,
      workshop: (form.workshop || "").trim(),
      service_type: form.service_type || "Other",
      description: (form.description || "").trim(),
      odometer_km: Number(form.odometer_km) || 0,
      cost: Number(form.cost) || 0,
      next_service_km: Number(form.next_service_km) || 0,
      next_service_date: form.next_service_date || "",
      invoice_url: form.invoice_url || "",
      invoice_public_id: form.invoice_public_id || "",
    };
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/admin/vehicles/${vehicle.id}/services/${form.id}`, payload);
        toast.success("Service updated");
      } else {
        await api.post(`/admin/vehicles/${vehicle.id}/services`, payload);
        toast.success("Service logged");
      }
      // refresh
      const { data } = await api.get(`/admin/vehicles/${vehicle.id}/services`);
      setLogs(data);
      setShowForm(false);
      onDirty && onDirty();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/admin/vehicles/${vehicle.id}/services/${confirmDelete.id}`);
      toast.success("Service log removed");
      const { data } = await api.get(`/admin/vehicles/${vehicle.id}/services`);
      setLogs(data);
      setConfirmDelete(null);
      onDirty && onDirty();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#fbf8f1] max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="veh-services-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-3">
            <Wrench size={22} className="text-[#0D5C63]" />
            Service History — {vehicle?.registration_number}
          </DialogTitle>
          <DialogDescription>
            {vehicle?.make} {vehicle?.model_name} · Track every service, repair and workshop visit to see the true cost of ownership.
          </DialogDescription>
        </DialogHeader>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-4" data-testid="tco-tile-purchase">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Purchase Price</div>
            <div className="font-serif text-xl text-[#1c1a17] mt-1">{fmtInr(tco.purchase)}</div>
          </div>
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-4" data-testid="tco-tile-service">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">Service Spend ({tco.service_count})</div>
            <div className="font-serif text-xl text-[#B8860B] mt-1">{fmtInr(tco.service_cost)}</div>
          </div>
          <div className="bg-[#234333] text-white rounded-sm p-4" data-testid="tco-tile-total">
            <div className="text-[10px] tracking-[0.2em] uppercase text-white/70 font-semibold flex items-center gap-1"><TrendingUp size={11} />Total Cost of Ownership</div>
            <div className="font-serif text-xl mt-1">{fmtInr(tco.total)}</div>
          </div>
          <div className="bg-white border border-[#e5e0d8] rounded-sm p-4" data-testid="tco-tile-next">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold flex items-center gap-1"><Gauge size={11} />Next Service</div>
            <div className="text-sm text-[#1c1a17] mt-1">{tco.next_service_km ? `${Number(tco.next_service_km).toLocaleString()} km` : "—"}</div>
            <div className="text-[11px] text-[#8a8478] mt-0.5">{tco.next_service_date || (tco.latest_date ? `after ${tco.latest_date}` : "no history yet")}</div>
          </div>
        </div>

        {canEdit && !showForm && (
          <div className="flex justify-end mt-4">
            <button type="button" onClick={openAdd} data-testid="svc-add-btn" className="inline-flex items-center gap-2 bg-[#0D5C63] hover:bg-[#083E43] text-white px-4 py-2 text-sm font-semibold rounded-sm">
              <Plus size={16} /> Log Service
            </button>
          </div>
        )}

        {showForm && (
          <div className="bg-white border border-[#0D5C63]/30 rounded-sm p-4 mt-4" data-testid="svc-form">
            <div className="flex items-center justify-between mb-3">
              <div className="font-serif text-lg text-[#1c1a17]">{form.id ? "Edit Service" : "Log New Service"}</div>
              <button type="button" onClick={() => setShowForm(false)} aria-label="Close" className="text-[#6b655b] hover:text-[#1c1a17] p-1"><X size={16} /></button>
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <Field label="Date *">
                <input type="date" data-testid="svc-input-date" className={inputCls} value={form.date} onChange={(e) => set("date", e.target.value)} />
              </Field>
              <Field label="Service Type">
                <select data-testid="svc-input-type" className={inputCls} value={form.service_type} onChange={(e) => set("service_type", e.target.value)}>
                  {SERVICE_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </Field>
              <Field label="Workshop">
                <input data-testid="svc-input-workshop" className={inputCls} value={form.workshop} onChange={(e) => set("workshop", e.target.value)} />
              </Field>
              <Field label="Odometer (km)">
                <input type="number" min="0" step="0.1" data-testid="svc-input-odo" className={inputCls} value={form.odometer_km} onChange={(e) => set("odometer_km", e.target.value)} />
              </Field>
              <Field label="Cost (₹)">
                <input type="number" min="0" step="0.01" data-testid="svc-input-cost" className={inputCls} value={form.cost} onChange={(e) => set("cost", e.target.value)} />
              </Field>
              <Field label="Next Service — km">
                <input type="number" min="0" step="0.1" data-testid="svc-input-next-km" className={inputCls} value={form.next_service_km} onChange={(e) => set("next_service_km", e.target.value)} />
              </Field>
              <Field label="Next Service — Date">
                <input type="date" data-testid="svc-input-next-date" className={inputCls} value={form.next_service_date} onChange={(e) => set("next_service_date", e.target.value)} />
              </Field>
              <Field label="Description / Work Done" full>
                <textarea data-testid="svc-input-desc" rows={3} maxLength={2000} className={`${inputCls} resize-y`} value={form.description} onChange={(e) => set("description", e.target.value)} placeholder="Oil + filter change, brake pads replaced, tyre rotation…" />
              </Field>
              <Field label="Invoice / Bill" full>
                <FileUploader
                  kind="vehicle_document"
                  label=""
                  url={form.invoice_url}
                  onChange={({ url, public_id }) => setForm((f) => ({ ...f, invoice_url: url || "", invoice_public_id: public_id || "" }))}
                  testid="svc-input-invoice"
                />
              </Field>
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button type="button" onClick={() => setShowForm(false)} className="text-sm px-4 py-2 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]">Cancel</button>
              <button type="button" onClick={save} disabled={saving} data-testid="svc-save-btn" className="text-sm bg-[#0D5C63] hover:bg-[#083E43] text-white px-4 py-2 rounded-sm font-semibold disabled:opacity-60">
                {saving ? "Saving…" : (form.id ? "Update" : "Save Service")}
              </button>
            </div>
          </div>
        )}

        <div className="mt-4">
          <div className="text-[10px] tracking-[0.25em] uppercase text-[#6b655b] font-semibold mb-2">Service Log</div>
          {loading ? (
            <div className="text-sm text-[#6b655b]">Loading…</div>
          ) : logs.length === 0 ? (
            <div className="bg-white border border-dashed border-[#e5e0d8] rounded-sm p-8 text-center">
              <Wrench size={28} className="mx-auto text-[#bcb6aa]" strokeWidth={1.2} />
              <div className="mt-2 text-sm text-[#6b655b]">No services logged yet.</div>
            </div>
          ) : (
            <div className="bg-white border border-[#e5e0d8] rounded-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#f5f1e8] text-[10px] tracking-widest uppercase text-[#6b655b]">
                  <tr>
                    <th className="text-left px-3 py-2">Date</th>
                    <th className="text-left px-3 py-2">Type</th>
                    <th className="text-left px-3 py-2">Workshop</th>
                    <th className="text-right px-3 py-2">Odometer</th>
                    <th className="text-right px-3 py-2">Cost</th>
                    <th className="text-left px-3 py-2">Next</th>
                    <th className="text-left px-3 py-2">Bill</th>
                    {canEdit && <th className="text-right px-3 py-2">Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#f0ece2]">
                  {logs.map((l) => (
                    <tr key={l.id} data-testid={`svc-row-${l.id}`}>
                      <td className="px-3 py-2 font-semibold text-[#1c1a17]">{l.date}</td>
                      <td className="px-3 py-2">{l.service_type || "—"}</td>
                      <td className="px-3 py-2">
                        <div>{l.workshop || "—"}</div>
                        {l.description && <div className="text-[11px] text-[#8a8478] line-clamp-1">{l.description}</div>}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{Number(l.odometer_km || 0).toLocaleString()}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtInr(l.cost)}</td>
                      <td className="px-3 py-2 text-[12px]">
                        {l.next_service_km ? `${Number(l.next_service_km).toLocaleString()} km` : ""}
                        {l.next_service_date ? <div className="text-[10px] text-[#8a8478]">{l.next_service_date}</div> : null}
                      </td>
                      <td className="px-3 py-2">
                        {l.invoice_url ? (
                          <a href={l.invoice_url} target="_blank" rel="noopener noreferrer" className="text-[#0D5C63] hover:underline text-[11px]">View</a>
                        ) : <span className="text-[#a09789]">—</span>}
                      </td>
                      {canEdit && (
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          <button type="button" onClick={() => openEdit(l)} data-testid={`svc-edit-${l.id}`} className="inline-flex items-center gap-1 text-[#234333] hover:bg-[#234333]/10 text-[11px] font-semibold px-2 py-1 rounded-sm"><Pencil size={11} /></button>
                          <button type="button" onClick={() => setConfirmDelete(l)} data-testid={`svc-delete-${l.id}`} className="inline-flex items-center gap-1 text-red-700 hover:bg-red-50 text-[11px] font-semibold px-2 py-1 rounded-sm ml-1"><Trash2 size={11} /></button>
                        </td>
                      )}
                    </tr>
                  ))}
                  <tr className="bg-[#faf8f2] font-semibold">
                    <td colSpan={4} className="px-3 py-2 text-right text-[10px] tracking-widest uppercase text-[#6b655b]">Total Service Spend</td>
                    <td className="px-3 py-2 text-right tabular-nums">{fmtInr(tco.service_cost)}</td>
                    <td colSpan={canEdit ? 3 : 2}></td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>

        <DialogFooter>
          <button type="button" onClick={onClose} className="text-sm px-4 py-2.5 rounded-sm border border-[#e5e0d8] text-[#6b655b] hover:bg-[#f5f1e8]" data-testid="svc-close">Close</button>
        </DialogFooter>

        <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
          <AlertDialogContent className="bg-[#fbf8f1]">
            <AlertDialogHeader>
              <AlertDialogTitle className="font-serif text-2xl">Remove service log?</AlertDialogTitle>
              <AlertDialogDescription>
                The entry from <b>{confirmDelete?.date}</b> ({fmtInr(confirmDelete?.cost)}) will be permanently removed.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove} data-testid="svc-confirm-delete" className="bg-red-700 hover:bg-red-800 text-white">Delete</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
}
